import { MaxSizeSpinner } from '@/components/loading_states/generic-spinner'
import { ProfileItem } from '@/components/profile-item'
import { Can } from '@/components/providers/ability-provider'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatCurrencyInPHP } from '@/lib/utils/generic-utils'
import { useTRPC } from '@/trpc/react'
import { getTypedEffectiveSettingValue } from '@repo/shared_utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  Building2,
  Calendar,
  Check,
  CreditCard,
  DollarSign,
  Eye,
  EyeOff,
  Fingerprint,
  Heart,
  Mail,
  MapPin,
  Phone,
  User,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'

export const Route = createFileRoute('/dashboard/employee/employee_profile/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  // const { user } = useAuth()
  const [isRateVisible, setIsRateVisible] = useState(false)
  const { data: employee, isPending } = useQuery({
    ...api.employee.getEmployeeMe.queryOptions(),
  })

  // Fetch effective settings for today's date
  const todayDate = DateTime.now().toISODate() ?? ''
  const { data: effectiveSettings } = useQuery({
    ...api.effectiveSettings.getOwnEffectiveSettingsForDate.queryOptions({
      date: todayDate,
    }),
    enabled: !!employee?.userId,
  })

  if (isPending || !employee) return <MaxSizeSpinner />

  return (
    <Can do="read" on="Employee">
      <div className="container py-10">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback>
                  {employee.firstName[0]}
                  {employee.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">
                  {employee.firstName} {employee.middleName} {employee.lastName}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {employee.position || 'Position not set'}
                </p>
              </div>
            </div>
          </CardHeader>
          <Separator className="mb-4" />
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Personal Information</h3>
                <div className="space-y-4">
                  <ProfileItem icon={Mail} label="Email" value={employee.email} />
                  <ProfileItem icon={Phone} label="Phone" value={employee.phoneNumber ?? ''} />
                  <ProfileItem icon={MapPin} label="Address" value={employee.address ?? ''} />
                  <ProfileItem
                    icon={User}
                    label="Gender"
                    value={
                      employee.gender
                        ? employee.gender.charAt(0).toUpperCase() + employee.gender.slice(1)
                        : null
                    }
                  />
                  <ProfileItem
                    icon={Calendar}
                    label="Birthdate"
                    value={
                      employee.birthdate
                        ? DateTime.fromISO(employee.birthdate).toLocaleString()
                        : null
                    }
                  />
                  <ProfileItem
                    icon={Heart}
                    label="Civil Status"
                    value={employee.civilStatus ?? ''}
                  />
                  {employee.civilStatus?.toLowerCase() === 'married' && (
                    <ProfileItem
                      icon={Heart}
                      label="Name of Spouse"
                      value={employee.nameOfSpouse ?? ''}
                    />
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Employment Details</h3>
                <div className="space-y-4">
                  <ProfileItem
                    icon={Fingerprint}
                    label="Internal Employee ID"
                    value={employee.internalEmployeeId ?? ''}
                  />
                  <ProfileItem
                    icon={Building2}
                    label="Department"
                    value={employee.department ?? ''}
                  />
                  <ProfileItem icon={User} label="Position" value={employee.position ?? ''} />
                  <ProfileItem
                    icon={Calendar}
                    label="Hire Date"
                    value={
                      employee.hireDate
                        ? DateTime.fromISO(employee.hireDate).toLocaleString()
                        : null
                    }
                  />
                  {employee.terminationDate && (
                    <ProfileItem
                      icon={Calendar}
                      label="Termination Date"
                      // value={format(new Date(employee.body.terminationDate), 'PPP')}
                      // value={format(new Date(employee.terminationDate), 'PPP')}
                      value={DateTime.fromISO(employee.terminationDate).toLocaleString(
                        DateTime.DATE_FULL,
                      )}
                    />
                  )}
                  <ProfileItem
                    icon={DollarSign}
                    label="Pay Basis (Current Day)"
                    value={
                      getTypedEffectiveSettingValue(effectiveSettings, 'pay_basis')
                        ?.replace('_', ' ')
                        .toUpperCase() ?? null
                    }
                  />
                  {getTypedEffectiveSettingValue(effectiveSettings, 'pay_basis') === 'hourly' && (
                    <div className="flex items-center gap-2">
                      <ProfileItem
                        icon={DollarSign}
                        label="Rate (Current Day)"
                        value={
                          isRateVisible &&
                          getTypedEffectiveSettingValue(effectiveSettings, 'hourly_rate')
                            ? `${formatCurrencyInPHP(
                                Number(
                                  getTypedEffectiveSettingValue(
                                    effectiveSettings,
                                    'hourly_rate',
                                  ) ?? 0,
                                ),
                              )}/hour`
                            : '••••••'
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsRateVisible(!isRateVisible)}
                        className="h-8 w-8"
                      >
                        {isRateVisible ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  )}
                  {getTypedEffectiveSettingValue(effectiveSettings, 'pay_basis') === 'daily' && (
                    <div className="flex items-center gap-2">
                      <ProfileItem
                        icon={DollarSign}
                        label="Rate (Current Day)"
                        value={
                          isRateVisible &&
                          getTypedEffectiveSettingValue(effectiveSettings, 'daily_rate')
                            ? `${formatCurrencyInPHP(
                                Number(
                                  getTypedEffectiveSettingValue(effectiveSettings, 'daily_rate') ??
                                    0,
                                ),
                              )}/day`
                            : '••••••'
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsRateVisible(!isRateVisible)}
                        className="h-8 w-8"
                      >
                        {isRateVisible ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  )}
                  {getTypedEffectiveSettingValue(effectiveSettings, 'pay_basis') === 'monthly' && (
                    <div className="flex items-center gap-2">
                      <ProfileItem
                        icon={DollarSign}
                        label="Rate (Current Day)"
                        value={
                          isRateVisible &&
                          getTypedEffectiveSettingValue(effectiveSettings, 'monthly_rate')
                            ? `${formatCurrencyInPHP(
                                Number(
                                  getTypedEffectiveSettingValue(
                                    effectiveSettings,
                                    'monthly_rate',
                                  ) ?? 0,
                                ),
                              )}/month`
                            : '••••••'
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsRateVisible(!isRateVisible)}
                        className="h-8 w-8"
                      >
                        {isRateVisible ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  )}
                  <ProfileItem
                    icon={CreditCard}
                    label="Payment Method"
                    value={employee.paymentMethod?.replace('_', ' ').toUpperCase() ?? null}
                  />
                  <ProfileItem
                    icon={CreditCard}
                    label="Payment Details"
                    value={employee.paymentDetails ?? null}
                  />
                  <ProfileItem
                    icon={Check}
                    label="Status"
                    value={
                      employee.isEmployeeVerifiedByAdmin ? 'Verified' : 'Pending Verification'
                    }
                    valueClassName={
                      employee.isEmployeeVerifiedByAdmin ? 'text-green-600' : 'text-yellow-600'
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 space-y-6">
              {/* <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  <GraduationCap className="mr-2 inline-block h-5 w-5" />
                  Educational Background
                </h3>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>School</TableHead>
                        <TableHead>Degree</TableHead>
                        <TableHead>Year Graduated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employee.educationalAttainments?.map((edu) => (
                        <TableRow key={`${edu.school}-${edu.yearGraduated}`}>
                          <TableCell>{edu.school}</TableCell>
                          <TableCell>{edu.degree}</TableCell>
                          <TableCell>{edu.yearGraduated}</TableCell>
                        </TableRow>
                      ))}
                      {(!employee.educationalAttainments ||
                        employee.educationalAttainments.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            No educational background information available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div> */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  <Briefcase className="mr-2 inline-block h-5 w-5" />
                  Previous Employment
                </h3>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company Name</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Period of Employment</TableHead>
                        <TableHead>Reason for Leaving</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employee.previousEmployments?.map((emp) => (
                        <TableRow key={`${emp.companyName}-${emp.position}`}>
                          <TableCell>{emp.companyName}</TableCell>
                          <TableCell>{emp.position}</TableCell>
                          <TableCell>{emp.periodOfEmployment}</TableCell>
                          <TableCell>{emp.reasonForLeaving || '--'}</TableCell>
                        </TableRow>
                      ))}
                      {(!employee.previousEmployments ||
                        employee.previousEmployments.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">
                            No previous employment information available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div> */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-semibold">Government Contributions Information</h3>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Organization ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employee.governmentContributionsOrgs?.map((org) => (
                        <TableRow key={`${org.organization}-${org.organizationId}`}>
                          <TableCell>{org.organization}</TableCell>
                          <TableCell>{org.organizationId}</TableCell>
                        </TableRow>
                      ))}
                      {(!employee.governmentContributionsOrgs ||
                        employee.governmentContributionsOrgs.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center text-muted-foreground">
                            No government contributions information available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div> */}
              {employee.otherDetails && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Other Details</h3>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm">{employee.otherDetails}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Can>
  )
}
